import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiMessageCircle,
  FiSend,
  FiUsers,
  FiSmile,
  FiPaperclip,
  FiMoreVertical,
  FiSearch,
  FiChevronLeft,
  FiCornerUpLeft,
  FiX,
  FiHeart,
  FiThumbsUp,
  FiThumbsDown,
  FiZap,
  FiStar,
  FiPlus,
  FiUser,
  FiHash,
  FiSettings,
  FiUserPlus,
  FiEdit3,
  FiTrash2,
  FiClock,
  FiCheck,
  FiCheckCircle,
  FiPhone,
  FiVideo,
  FiInfo,
  FiMic,
  FiImage,
  FiFile,
  FiChevronDown,
  FiGlobe,
  FiArrowLeft,
  FiMenu
} = FiIcons;

const ChatRoom = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeChat, setActiveChat] = useState(null);
  const [chatType, setChatType] = useState('group'); // 'group' or 'direct'
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatType, setNewChatType] = useState('group');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const chatAreaRef = useRef(null);

  // Sample group chats
  const [groupChats] = useState([
    {
      id: 'general',
      name: 'General Chat',
      type: 'group',
      members: 156,
      description: 'General discussions and announcements',
      avatar: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=100&h=100&fit=crop',
      lastMessage: 'Good morning everyone! Hope you all are having a great day! 🌅',
      lastMessageTime: '9:15 AM',
      unreadCount: 3,
      isOnline: true,
      category: 'public'
    },
    {
      id: 'study-help',
      name: 'Study Help',
      type: 'group',
      members: 89,
      description: 'Get help with your studies',
      avatar: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=100&h=100&fit=crop',
      lastMessage: 'Can anyone help me with this algebra problem?',
      lastMessageTime: '8:45 AM',
      unreadCount: 7,
      isOnline: true,
      category: 'public'
    },
    {
      id: 'mathematics',
      name: 'Mathematics',
      type: 'group',
      members: 67,
      description: 'Math discussions and problem solving',
      avatar: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=100&h=100&fit=crop',
      lastMessage: 'The quadratic formula is really useful for...',
      lastMessageTime: 'Yesterday',
      unreadCount: 0,
      isOnline: true,
      category: 'subject'
    },
    {
      id: 'science',
      name: 'Science',
      type: 'group',
      members: 78,
      description: 'Science topics and experiments',
      avatar: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=100&h=100&fit=crop',
      lastMessage: 'Just finished my physics experiment!',
      lastMessageTime: 'Yesterday',
      unreadCount: 2,
      isOnline: true,
      category: 'subject'
    },
    {
      id: 'leadership',
      name: 'Leadership',
      type: 'group',
      members: 45,
      description: 'Leadership development and mentorship',
      avatar: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=100&h=100&fit=crop',
      lastMessage: 'Leadership workshop this Saturday!',
      lastMessageTime: '2 days ago',
      unreadCount: 1,
      isOnline: true,
      category: 'subject'
    }
  ]);

  // Sample direct chats
  const [directChats] = useState([
    {
      id: 'aminata-kargbo',
      name: 'Aminata Kargbo',
      type: 'direct',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
      status: 'online',
      lastMessage: 'Thanks for helping me with the math problem!',
      lastMessageTime: '10:30 AM',
      unreadCount: 0,
      isOnline: true,
      role: 'Math Tutor',
      category: 'direct'
    },
    {
      id: 'omar-jallow',
      name: 'Omar Jallow',
      type: 'direct',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      status: 'online',
      lastMessage: 'Are you joining the study group today?',
      lastMessageTime: '9:45 AM',
      unreadCount: 2,
      isOnline: true,
      role: 'Student',
      category: 'direct'
    },
    {
      id: 'fatou-conteh',
      name: 'Fatou Conteh',
      type: 'direct',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      status: 'away',
      lastMessage: 'I found a great resource for English literature',
      lastMessageTime: 'Yesterday',
      unreadCount: 1,
      isOnline: false,
      role: 'English Teacher',
      category: 'direct'
    },
    {
      id: 'isatu-bangura',
      name: 'Isatu Bangura',
      type: 'direct',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      status: 'busy',
      lastMessage: 'Let\'s schedule a mentoring session',
      lastMessageTime: '2 days ago',
      unreadCount: 0,
      isOnline: false,
      role: 'Community Leader',
      category: 'direct'
    }
  ]);

  // All chats combined
  const allChats = [...groupChats, ...directChats];

  // Sample messages for different chat types
  const [sampleMessages] = useState({
    'general': [
      {
        id: 1,
        user: {
          name: 'Aminata Kargbo',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: 'Good morning everyone! Hope you all are having a great day! 🌅',
        timestamp: '9:15 AM',
        type: 'text',
        reactions: [
          { emoji: '👍', count: 3, users: ['Omar Jallow', 'Fatou Conteh', 'Lamin Jobe'] },
          { emoji: '😊', count: 2, users: ['Isatu Bangura', 'Mariama Sesay'] }
        ],
        replies: []
      },
      {
        id: 2,
        user: {
          name: 'Omar Jallow',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: "Morning Aminata! Ready for today's leadership workshop? 💪",
        timestamp: '9:18 AM',
        type: 'text',
        reactions: [
          { emoji: '👍', count: 2, users: ['Aminata Kargbo', 'Lamin Jobe'] }
        ],
        replies: []
      },
      {
        id: 3,
        user: {
          name: 'Fatou Conteh',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
          status: 'away'
        },
        message: "I'll be joining the workshop as well! Does anyone have the link to the materials we need to review beforehand?",
        timestamp: '9:22 AM',
        type: 'text',
        reactions: [],
        replies: []
      },
      {
        id: 4,
        user: {
          name: 'Isatu Bangura',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
          status: 'busy'
        },
        message: "Here's the link to the workshop materials: https://ilmglobal.org/resources/leadership-workshop",
        timestamp: '9:30 AM',
        type: 'text',
        reactions: [
          { emoji: '🙏', count: 4, users: ['Omar Jallow', 'Fatou Conteh', 'Aminata Kargbo', 'Lamin Jobe'] }
        ],
        replies: []
      },
      {
        id: 5,
        user: {
          name: 'Lamin Jobe',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: "Has anyone seen the latest study resources on the history section? They've added some great content on African independence movements.",
        timestamp: '9:45 AM',
        type: 'text',
        reactions: [],
        replies: []
      }
    ],
    'study-help': [
      {
        id: 1,
        user: {
          name: 'Ibrahim Sanneh',
          avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: 'Can anyone help me with this algebra problem? I need to solve for x: 3x² + 5x - 2 = 0',
        timestamp: '8:45 AM',
        type: 'text',
        reactions: [],
        replies: []
      },
      {
        id: 2,
        user: {
          name: 'Aminata Kargbo',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: "You can use the quadratic formula: x = (-b ± √(b² - 4ac)) / 2a where a=3, b=5, and c=-2",
        timestamp: '8:50 AM',
        type: 'text',
        reactions: [
          { emoji: '👍', count: 3, users: ['Ibrahim Sanneh', 'Omar Jallow', 'Fatou Conteh'] }
        ],
        replies: []
      },
      {
        id: 3,
        user: {
          name: 'Ibrahim Sanneh',
          avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: "Thank you! I'll try that now.",
        timestamp: '8:55 AM',
        type: 'text',
        reactions: [],
        replies: []
      }
    ],
    'aminata-kargbo': [
      {
        id: 1,
        user: {
          name: 'Aminata Kargbo',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: 'Hi! I saw your question about quadratic equations in the math group. I can help you with that!',
        timestamp: '10:15 AM',
        type: 'text',
        reactions: [],
        replies: []
      },
      {
        id: 2,
        user: {
          name: user?.name || 'You',
          avatar: user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: 'That would be amazing! I\'m really struggling with the discriminant part.',
        timestamp: '10:17 AM',
        type: 'text',
        reactions: [],
        replies: []
      },
      {
        id: 3,
        user: {
          name: 'Aminata Kargbo',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: 'No problem! The discriminant (b² - 4ac) tells us about the nature of the roots. Let me explain step by step...',
        timestamp: '10:20 AM',
        type: 'text',
        reactions: [
          { emoji: '❤️', count: 1, users: [user?.name || 'You'] }
        ],
        replies: []
      }
    ]
  });

  // Chat categories
  const chatCategories = [
    { id: 'all', name: 'All Chats' },
    { id: 'direct', name: 'Direct Messages' },
    { id: 'public', name: 'Public Channels' },
    { id: 'subject', name: 'Subject Groups' }
  ];

  const emojis = [
    { emoji: '👍', name: 'thumbs up' },
    { emoji: '❤️', name: 'heart' },
    { emoji: '😂', name: 'laugh' },
    { emoji: '😮', name: 'wow' },
    { emoji: '👏', name: 'clap' },
    { emoji: '🎉', name: 'celebrate' },
    { emoji: '🔥', name: 'fire' },
    { emoji: '✨', name: 'sparkle' },
    { emoji: '😊', name: 'smile' },
    { emoji: '💪', name: 'strong' },
    { emoji: '🤔', name: 'thinking' },
    { emoji: '📚', name: 'books' }
  ];

  // Initialize chat from navigation state
  useEffect(() => {
    if (location.state?.chatPartner) {
      const partner = location.state.chatPartner;
      setActiveChat(partner.id || partner.name.toLowerCase().replace(' ', '-'));
      setChatType('direct');
    } else if (location.state?.groupChat) {
      setActiveChat(location.state.groupChat);
      setChatType('group');
    } else {
      // Default to general chat
      setActiveChat('general');
      setChatType('group');
    }
  }, [location]);

  // Load messages when active chat changes
  useEffect(() => {
    if (activeChat) {
      const chatMessages = sampleMessages[activeChat] || [];
      setMessages(chatMessages);
    }
  }, [activeChat, sampleMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      if (isMobileView) {
        setShowSidebar(false);
      } else {
        setShowSidebar(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simulate typing indicator
  useEffect(() => {
    if (message.length > 0) {
      setIsTyping(true);
    } else {
      setIsTyping(false);
    }
  }, [message]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      const newMessage = {
        id: Date.now(),
        user: {
          name: user?.name || 'You',
          avatar: user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
          status: 'online'
        },
        message: message.trim(),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: 'text',
        reactions: [],
        replies: []
      };

      if (replyingTo) {
        newMessage.replyTo = replyingTo.id;
        setReplyingTo(null);
      }

      setMessages(prevMessages => [...prevMessages, newMessage]);
      setMessage('');

      // Update last message in chat list
      const updatedChat = allChats.find(chat => chat.id === activeChat);
      if (updatedChat) {
        updatedChat.lastMessage = message.trim();
        updatedChat.lastMessageTime = 'Just now';
      }
    }
  };

  const handleReaction = (messageId, emoji) => {
    setMessages(prevMessages => prevMessages.map(msg => {
      if (msg.id === messageId) {
        const existingReaction = msg.reactions.find(r => r.emoji === emoji);
        if (existingReaction) {
          const userReacted = existingReaction.users.includes(user?.name || 'You');
          if (userReacted) {
            if (existingReaction.count === 1) {
              return { ...msg, reactions: msg.reactions.filter(r => r.emoji !== emoji) };
            } else {
              return {
                ...msg,
                reactions: msg.reactions.map(r =>
                  r.emoji === emoji
                    ? {
                        ...r,
                        count: r.count - 1,
                        users: r.users.filter(u => u !== (user?.name || 'You'))
                      }
                    : r
                )
              };
            }
          } else {
            return {
              ...msg,
              reactions: msg.reactions.map(r =>
                r.emoji === emoji
                  ? {
                      ...r,
                      count: r.count + 1,
                      users: [...r.users, user?.name || 'You']
                    }
                  : r
              )
            };
          }
        } else {
          return {
            ...msg,
            reactions: [...msg.reactions, { emoji, count: 1, users: [user?.name || 'You'] }]
          };
        }
      }
      return msg;
    }));
    setShowEmojiPicker(null);
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    setMessage('');
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
        return 'bg-yellow-500';
      case 'busy':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleChatSelect = (chat) => {
    setActiveChat(chat.id);
    setChatType(chat.type);
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const getCurrentChat = () => {
    return allChats.find(chat => chat.id === activeChat);
  };

  const getFilteredChats = () => {
    if (!searchQuery && selectedCategory === 'all') return allChats;
    
    return allChats.filter(chat => {
      const matchesSearch = searchQuery === '' || 
        chat.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        chat.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || chat.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  };

  const handleCreateNewChat = () => {
    setShowNewChatModal(true);
  };

  const handleStartDirectChat = (userName) => {
    const chatId = userName.toLowerCase().replace(' ', '-');
    setActiveChat(chatId);
    setChatType('direct');
    setShowNewChatModal(false);
    addNotification({
      type: 'success',
      title: 'Direct Chat Started',
      message: `Started conversation with ${userName}`
    });
  };

  const handleCreateGroupChat = (groupName) => {
    const chatId = groupName.toLowerCase().replace(' ', '-');
    setActiveChat(chatId);
    setChatType('group');
    setShowNewChatModal(false);
    addNotification({
      type: 'success',
      title: 'Group Chat Created',
      message: `Created group: ${groupName}`
    });
  };

  const currentChat = getCurrentChat();

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="chat-container bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-full h-full flex flex-1">
        {/* Mobile Sidebar Toggle */}
        {isMobile && !showSidebar && (
          <button 
            onClick={() => setShowSidebar(true)} 
            className="absolute top-20 left-4 z-20 bg-white p-2 rounded-lg shadow-md"
          >
            <SafeIcon icon={FiMenu} className="w-5 h-5 text-primary-600" />
          </button>
        )}

        {/* Sidebar */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div
              initial={isMobile ? { x: -300 } : { opacity: 0 }}
              animate={isMobile ? { x: 0 } : { opacity: 1 }}
              exit={isMobile ? { x: -300 } : { opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={`${isMobile ? 'absolute inset-y-0 left-0 z-40 w-72' : 'relative w-72'} bg-gray-50 border-r border-gray-200 flex flex-col h-full`}
            >
              {/* Sidebar Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-lg font-bold text-gray-800 flex items-center">
                    <SafeIcon icon={FiMessageCircle} className="w-5 h-5 mr-2 text-primary-600" />
                    Messages
                  </h1>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={handleCreateNewChat} 
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" 
                      title="New chat"
                    >
                      <SafeIcon icon={FiPlus} className="w-5 h-5" />
                    </button>
                    {isMobile && (
                      <button 
                        onClick={() => setShowSidebar(false)} 
                        className="p-2 text-gray-500 hover:text-gray-700"
                      >
                        <SafeIcon icon={FiX} className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Search */}
                <div className="relative">
                  <SafeIcon 
                    icon={FiSearch} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" 
                  />
                  <input 
                    type="text" 
                    placeholder="Search chats..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    className="w-full h-10 pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" 
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')} 
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Chat Categories */}
              <div className="px-2 pt-3 pb-2 flex overflow-x-auto hide-scrollbar">
                {chatCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-3 py-1 mr-2 rounded-full text-xs font-medium whitespace-nowrap ${
                      selectedCategory === category.id 
                        ? 'bg-primary-100 text-primary-700' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Chat List */}
              <div className="flex-1 overflow-y-auto">
                {getFilteredChats().length > 0 ? (
                  getFilteredChats().map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => handleChatSelect(chat)}
                      className={`w-full text-left p-3 hover:bg-gray-100 transition-colors border-b border-gray-100 ${
                        activeChat === chat.id ? 'bg-primary-50 border-primary-200' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="relative flex-shrink-0">
                          <img 
                            src={chat.avatar} 
                            alt={chat.name} 
                            className="w-10 h-10 rounded-full object-cover" 
                          />
                          {chat.type === 'direct' && (
                            <div 
                              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(chat.status)}`}
                            ></div>
                          )}
                          {chat.type === 'group' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-primary-500 rounded-full border-2 border-white flex items-center justify-center">
                              <SafeIcon icon={chat.category === 'public' ? FiGlobe : FiHash} className="w-1.5 h-1.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-medium text-gray-800 text-sm truncate">{chat.name}</h3>
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 whitespace-nowrap">{chat.lastMessageTime}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500 truncate max-w-[140px]">
                              {chat.type === 'direct' ? '' : `${chat.type === 'group' ? '#' : '@'} `}
                              {chat.lastMessage}
                            </p>
                            {chat.unreadCount > 0 && (
                              <span className="bg-primary-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                {chat.unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No chats found. Try adjusting your search.
                  </div>
                )}
              </div>

              {/* User Profile */}
              <div className="p-3 border-t border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <img 
                      src={user?.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"} 
                      alt={user?.name || "You"} 
                      className="w-8 h-8 rounded-full object-cover" 
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{user?.name || "You"}</p>
                      <p className="text-xs text-gray-500">Online</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full">
                      <SafeIcon icon={FiSettings} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col h-full bg-gray-50" ref={chatAreaRef}>
          {currentChat ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {isMobile && (
                      <button 
                        onClick={() => setShowSidebar(true)} 
                        className="p-1.5 text-gray-600 hover:text-gray-800 rounded-lg"
                      >
                        <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                      </button>
                    )}
                    <div className="relative">
                      <img 
                        src={currentChat.avatar} 
                        alt={currentChat.name} 
                        className="w-10 h-10 rounded-full object-cover" 
                      />
                      {currentChat.type === 'direct' && (
                        <div 
                          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${getStatusColor(currentChat.status)}`}
                        ></div>
                      )}
                      {currentChat.type === 'group' && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-primary-500 rounded-full border-2 border-white flex items-center justify-center">
                          <SafeIcon icon={currentChat.category === 'public' ? FiGlobe : FiHash} className="w-1 h-1 text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-800">
                        {currentChat.type === 'group' && '#'}{currentChat.name}
                      </h2>
                      <p className="text-xs text-gray-500">
                        {currentChat.type === 'group' 
                          ? `${currentChat.members} members` 
                          : currentChat.status === 'online' 
                            ? 'Online' 
                            : `Last seen ${currentChat.lastMessageTime}`
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {currentChat.type === 'direct' && (
                      <>
                        <button className="p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100">
                          <SafeIcon icon={FiPhone} className="w-5 h-5" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100">
                          <SafeIcon icon={FiVideo} className="w-5 h-5" />
                        </button>
                      </>
                    )}
                    <button 
                      onClick={() => setShowChatInfo(!showChatInfo)} 
                      className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${showChatInfo ? 'text-primary-600' : 'text-gray-600 hover:text-primary-600'}`}
                    >
                      <SafeIcon icon={FiInfo} className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100">
                      <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Chat Area with Chat Info Sidebar */}
              <div className="flex-1 flex overflow-hidden">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {currentChat.type === 'group' && (
                    <div className="text-center py-5 px-4">
                      <div className="w-16 h-16 rounded-full bg-primary-100 mx-auto mb-3 flex items-center justify-center">
                        <SafeIcon icon={currentChat.category === 'public' ? FiGlobe : FiHash} className="w-8 h-8 text-primary-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 mb-1">Welcome to #{currentChat.name}</h3>
                      <p className="text-sm text-gray-600 max-w-md mx-auto mb-3">{currentChat.description}</p>
                      <div className="text-xs text-gray-500">
                        Channel created • {currentChat.members} members
                      </div>
                    </div>
                  )}

                  {messages.map((msg, index) => {
                    // Check if this message is the first of a sequence from the same user
                    const isFirstInSequence = index === 0 || messages[index - 1].user.name !== msg.user.name;
                    // Check if this message is the last of a sequence from the same user
                    const isLastInSequence = index === messages.length - 1 || messages[index + 1].user.name !== msg.user.name;
                    
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`group flex items-start space-x-2 ${
                          msg.user.name === (user?.name || 'You') ? 'justify-end' : ''
                        } ${isFirstInSequence ? 'mt-4' : 'mt-1'}`}
                      >
                        {msg.user.name !== (user?.name || 'You') && isFirstInSequence && (
                          <div className="relative flex-shrink-0 mt-1">
                            <img 
                              src={msg.user.avatar} 
                              alt={msg.user.name} 
                              className="w-8 h-8 rounded-full object-cover" 
                            />
                            {msg.user.status && (
                              <div 
                                className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${getStatusColor(msg.user.status)}`}
                              ></div>
                            )}
                          </div>
                        )}
                        
                        <div 
                          className={`max-w-[75%] ${
                            msg.user.name === (user?.name || 'You') 
                              ? 'ml-auto' 
                              : isFirstInSequence ? '' : 'ml-10'
                          }`}
                        >
                          {isFirstInSequence && msg.user.name !== (user?.name || 'You') && (
                            <div className="flex items-center mb-1 ml-0.5">
                              <span className="text-xs font-medium text-gray-800">{msg.user.name}</span>
                              <span className="text-xs text-gray-500 ml-2">{msg.timestamp}</span>
                            </div>
                          )}
                          
                          {/* Reply reference */}
                          {msg.replyTo && (
                            <div 
                              className={`p-2 rounded-lg mb-1 text-xs border-l-2 ${
                                msg.user.name === (user?.name || 'You') 
                                  ? 'bg-primary-50 text-primary-800 border-primary-300' 
                                  : 'bg-gray-100 text-gray-600 border-gray-300'
                              }`}
                            >
                              <div className="flex items-center">
                                <SafeIcon icon={FiCornerUpLeft} className="w-3 h-3 mr-1" />
                                <span className="font-medium">Replying to message</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Message content */}
                          <div 
                            className={`p-3 rounded-2xl ${
                              msg.user.name === (user?.name || 'You')
                                ? 'bg-primary-500 text-white rounded-br-none'
                                : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                          
                          {/* Timestamp for user's messages */}
                          {msg.user.name === (user?.name || 'You') && isLastInSequence && (
                            <div className="text-xs text-gray-500 mt-1 text-right mr-1">
                              {msg.timestamp}
                              {index === messages.length - 1 && (
                                <SafeIcon icon={FiCheckCircle} className="w-3 h-3 ml-1 inline text-primary-500" />
                              )}
                            </div>
                          )}
                          
                          {/* Reactions */}
                          {msg.reactions && msg.reactions.length > 0 && (
                            <div 
                              className={`flex flex-wrap gap-1 mt-1 ${
                                msg.user.name === (user?.name || 'You') ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              {msg.reactions.map((reaction, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleReaction(msg.id, reaction.emoji)}
                                  className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs transition-all ${
                                    reaction.users.includes(user?.name || 'You')
                                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                  title={`${reaction.users.join(', ')} reacted with ${reaction.emoji}`}
                                >
                                  <span>{reaction.emoji}</span>
                                  <span className="font-medium">{reaction.count}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Message actions */}
                          <div 
                            className={`flex ${
                              msg.user.name === (user?.name || 'You') ? 'justify-end' : 'justify-start'
                            } opacity-0 group-hover:opacity-100 transition-opacity mt-1`}
                          >
                            <div className="flex space-x-1 bg-white border border-gray-200 rounded-lg shadow-sm p-1">
                              <button 
                                onClick={() => setShowEmojiPicker(showEmojiPicker === msg.id ? null : msg.id)} 
                                className="p-1 text-gray-400 hover:text-yellow-500 rounded hover:bg-gray-50 transition-colors"
                              >
                                <SafeIcon icon={FiSmile} className="w-3 h-3" />
                              </button>
                              <button 
                                onClick={() => handleReply(msg)} 
                                className="p-1 text-gray-400 hover:text-blue-500 rounded hover:bg-gray-50 transition-colors"
                              >
                                <SafeIcon icon={FiCornerUpLeft} className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Emoji Picker */}
                          {showEmojiPicker === msg.id && (
                            <div 
                              ref={emojiPickerRef} 
                              className={`absolute z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-2 mt-1 ${
                                msg.user.name === (user?.name || 'You') ? 'right-0' : 'left-0'
                              }`}
                            >
                              <div className="grid grid-cols-6 gap-1 max-w-[180px]">
                                {emojis.map((emojiObj, index) => (
                                  <button
                                    key={index}
                                    onClick={() => handleReaction(msg.id, emojiObj.emoji)}
                                    className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-lg transition-colors"
                                    title={emojiObj.name}
                                  >
                                    {emojiObj.emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}

                  {/* Typing indicator */}
                  {isTyping && activeChat === 'general' && (
                    <div className="flex items-start space-x-2 mt-2">
                      <div className="relative flex-shrink-0">
                        <img 
                          src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" 
                          alt="Fatou Conteh" 
                          className="w-8 h-8 rounded-full object-cover" 
                        />
                      </div>
                      <div className="bg-white text-gray-500 rounded-2xl p-3 rounded-bl-none border border-gray-200">
                        <div className="flex space-x-1 items-center h-5">
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"></div>
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Chat Info Sidebar */}
                <AnimatePresence>
                  {showChatInfo && (
                    <motion.div 
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: 250, opacity: 1 }}
                      exit={{ width: 0, opacity: 0 }}
                      className="border-l border-gray-200 bg-white overflow-y-auto"
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-medium text-gray-800">About</h3>
                          <button 
                            onClick={() => setShowChatInfo(false)} 
                            className="text-gray-400 hover:text-gray-600 p-1"
                          >
                            <SafeIcon icon={FiX} className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="text-center mb-6">
                          <img 
                            src={currentChat.avatar} 
                            alt={currentChat.name} 
                            className="w-16 h-16 rounded-full mx-auto mb-2 object-cover" 
                          />
                          <h4 className="font-medium text-gray-800">
                            {currentChat.type === 'group' && '#'}{currentChat.name}
                          </h4>
                          <p className="text-xs text-gray-500">{currentChat.description || 'No description'}</p>
                        </div>

                        {currentChat.type === 'group' && (
                          <>
                            <div className="mb-4">
                              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Members • {currentChat.members}</h4>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <img 
                                      src="https://images.unsplash.com/photo-1494790108755-2616b612b77c?w=100&h=100&fit=crop&crop=face" 
                                      alt="Aminata Kargbo" 
                                      className="w-6 h-6 rounded-full mr-2 object-cover" 
                                    />
                                    <span className="text-sm text-gray-800">Aminata Kargbo</span>
                                  </div>
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">Admin</span>
                                </div>
                                <div className="flex items-center">
                                  <img 
                                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" 
                                    alt="Omar Jallow" 
                                    className="w-6 h-6 rounded-full mr-2 object-cover" 
                                  />
                                  <span className="text-sm text-gray-800">Omar Jallow</span>
                                </div>
                                <div className="flex items-center">
                                  <img 
                                    src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" 
                                    alt="Fatou Conteh" 
                                    className="w-6 h-6 rounded-full mr-2 object-cover" 
                                  />
                                  <span className="text-sm text-gray-800">Fatou Conteh</span>
                                </div>
                                <button className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                                  View all members
                                </button>
                              </div>
                            </div>

                            <div className="mb-4">
                              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Media</h4>
                              <div className="grid grid-cols-3 gap-1">
                                <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                                  <img 
                                    src="https://images.unsplash.com/photo-1509228468518-180dd4864904?w=100&h=100&fit=crop" 
                                    alt="Shared media" 
                                    className="w-full h-full object-cover" 
                                  />
                                </div>
                                <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                                  <img 
                                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=100&h=100&fit=crop" 
                                    alt="Shared media" 
                                    className="w-full h-full object-cover" 
                                  />
                                </div>
                                <div className="aspect-square bg-gray-100 rounded overflow-hidden relative">
                                  <img 
                                    src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=100&h=100&fit=crop" 
                                    alt="Shared media" 
                                    className="w-full h-full object-cover" 
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                    <span className="text-white font-medium">+4</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {currentChat.type === 'direct' && (
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Role</h4>
                              <p className="text-sm text-gray-800">{currentChat.role || 'Member'}</p>
                            </div>
                            <div>
                              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Status</h4>
                              <div className="flex items-center">
                                <div className={`w-2 h-2 rounded-full ${getStatusColor(currentChat.status)} mr-2`}></div>
                                <span className="text-sm text-gray-800 capitalize">{currentChat.status}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="pt-4 mt-4 border-t border-gray-100">
                          {currentChat.type === 'direct' ? (
                            <button className="w-full py-2 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium transition-colors flex items-center justify-center">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                              Delete Conversation
                            </button>
                          ) : (
                            <button className="w-full py-2 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium transition-colors flex items-center justify-center">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
                              Leave Channel
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Reply Preview */}
              {replyingTo && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-4 pt-3 border-t border-gray-100 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm text-primary-600">
                      <SafeIcon icon={FiCornerUpLeft} className="w-4 h-4 mr-1" />
                      <span>Replying to <span className="font-medium">{replyingTo.user.name}</span></span>
                    </div>
                    <button 
                      onClick={cancelReply} 
                      className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="pl-5 border-l-2 border-primary-300 py-2 mb-3">
                    <p className="text-xs text-gray-600 line-clamp-2">{replyingTo.message}</p>
                  </div>
                </motion.div>
              )}

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                  <button 
                    type="button" 
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100"
                  >
                    <SafeIcon icon={FiPaperclip} className="w-5 h-5" />
                  </button>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={replyingTo ? `Reply to ${replyingTo.user.name}...` : `Message ${currentChat.type === 'group' ? '#' + currentChat.name : currentChat.name}...`}
                      className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                      <button 
                        type="button" 
                        className="p-1.5 text-gray-400 hover:text-yellow-500 rounded transition-colors"
                      >
                        <SafeIcon icon={FiSmile} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100"
                  >
                    <SafeIcon icon={FiMic} className="w-5 h-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={!message.trim()}
                    className={`p-2 rounded-lg font-medium transition-colors ${
                      message.trim()
                        ? 'bg-primary-500 text-white hover:bg-primary-600'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <SafeIcon icon={FiMessageCircle} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Welcome to Chat</h3>
                <p className="text-gray-600">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>

        {/* New Chat Modal */}
        <AnimatePresence>
          {showNewChatModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowNewChatModal(false)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">New Chat</h2>
                  <button 
                    onClick={() => setShowNewChatModal(false)} 
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6" />
                  </button>
                </div>

                {/* Chat Type Selection */}
                <div className="mb-6">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setNewChatType('direct')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        newChatType === 'direct'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <SafeIcon icon={FiUser} className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-medium">Direct Message</p>
                      <p className="text-xs text-gray-500">Chat with one person</p>
                    </button>
                    <button
                      onClick={() => setNewChatType('group')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        newChatType === 'group'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <SafeIcon icon={FiUsers} className="w-6 h-6 mx-auto mb-2" />
                      <p className="font-medium">Group Chat</p>
                      <p className="text-xs text-gray-500">Chat with multiple people</p>
                    </button>
                  </div>
                </div>

                {newChatType === 'direct' ? (
                  <div className="space-y-3">
                    <h3 className="font-medium text-gray-800">Select a person to chat with:</h3>
                    {['Aminata Kargbo', 'Omar Jallow', 'Fatou Conteh', 'Isatu Bangura'].map((name) => (
                      <button
                        key={name}
                        onClick={() => handleStartDirectChat(name)}
                        className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <SafeIcon icon={FiUser} className="w-4 h-4 text-primary-600" />
                          </div>
                          <span className="font-medium text-gray-800">{name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Group Name
                      </label>
                      <input
                        type="text"
                        placeholder="Enter group name..."
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            handleCreateGroupChat(e.target.value.trim());
                          }
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Group Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button className="p-3 border border-primary-300 bg-primary-50 text-primary-700 rounded-lg text-center">
                          <SafeIcon icon={FiHash} className="w-5 h-5 mx-auto mb-1" />
                          <div className="font-medium">Subject Group</div>
                        </button>
                        <button className="p-3 border border-gray-200 text-gray-700 rounded-lg text-center">
                          <SafeIcon icon={FiGlobe} className="w-5 h-5 mx-auto mb-1" />
                          <div className="font-medium">Public Channel</div>
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const input = document.querySelector('input[placeholder="Enter group name..."]');
                        if (input.value.trim()) {
                          handleCreateGroupChat(input.value.trim());
                        }
                      }}
                      className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-600 transition-colors"
                    >
                      Create Group
                    </button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatRoom;